import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/shared/lib/api'

// ── Dashboard ───────────────────────────────────────────────
export function useDashboard() {
  return useQuery({
    queryKey: ['gfo', 'dashboard'],
    queryFn: async () => {
      const { data } = await api.get('/gfo/dashboard')
      return data.data
    },
  })
}

// ── Keywords ────────────────────────────────────────────────
export function useKeywords(page = 1, sortBy = 'opportunity_score') {
  return useQuery({
    queryKey: ['gfo', 'keywords', page, sortBy],
    queryFn: async () => {
      const { data } = await api.get('/gfo/keywords', {
        params: { page, per_page: 20, sort_by: sortBy },
      })
      return { keywords: data.data, meta: data.meta }
    },
  })
}

// ── Reviews ─────────────────────────────────────────────────
export function useReviews(page = 1, filters?: { rating?: number; needs_response?: boolean }) {
  return useQuery({
    queryKey: ['gfo', 'reviews', page, filters],
    queryFn: async () => {
      const { data } = await api.get('/gfo/reviews', {
        params: { page, per_page: 20, ...filters },
      })
      return { reviews: data.data, meta: data.meta }
    },
  })
}

export function useGenerateReply() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (reviewId: string) => {
      const { data } = await api.post(`/gfo/reviews/${reviewId}/generate-reply`)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gfo', 'reviews'] }),
  })
}

export function useApplyReply() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ reviewId, replyText }: { reviewId: string; replyText: string }) => {
      const { data } = await api.post(`/gfo/reviews/${reviewId}/reply`, { reply_text: replyText })
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gfo'] }),
  })
}

// ── Recommendations ─────────────────────────────────────────
export function useRecommendations(page = 1, status?: string) {
  return useQuery({
    queryKey: ['gfo', 'recommendations', page, status],
    queryFn: async () => {
      const { data } = await api.get('/gfo/recommendations', {
        params: { page, per_page: 20, status },
      })
      return { recommendations: data.data, meta: data.meta }
    },
  })
}

export function useApplyRecommendation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (recoId: string) => {
      const { data } = await api.post(`/gfo/recommendations/${recoId}/apply`)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gfo'] }),
  })
}

export function useDismissRecommendation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (recoId: string) => {
      const { data } = await api.post(`/gfo/recommendations/${recoId}/dismiss`)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gfo'] }),
  })
}

// ── Accounts ────────────────────────────────────────────────
export function useGoogleAccounts() {
  return useQuery({
    queryKey: ['gfo', 'accounts'],
    queryFn: async () => {
      const { data } = await api.get('/gfo/accounts')
      return data.data
    },
  })
}

// ── Sync ────────────────────────────────────────────────────
export function useTriggerSync() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/gfo/sync')
      return data.data
    },
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: ['gfo'] }), 2000)
    },
  })
}
